import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import VehicleCard from '../components/VehicleCard'
import CMSGallery from '../components/CMSGallery'
import { fetchVehicles } from '../lib/cms'

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState('Alle')

  useEffect(() => {
    let isMounted = true
    async function load() {
      setLoading(true)
      const data = await fetchVehicles()
      if (isMounted) {
        setVehicles(data)
        setLoading(false)
      }
    }
    load()
    return () => { isMounted = false }
  }, [])

  const categories = ['Alle', 'Temperaturstyret transport', 'Ekspres & Godstransport']

  const filteredVehicles = selectedCategory === 'Alle'
    ? vehicles
    : vehicles.filter(v => v.category === selectedCategory)

  return (
    <div className="space-y-0">
      <SEO
        title="Vores biler & transportflåde - Transport4U"
        description="Se Transport4U's vognpark af nye Mercedes-Benz varevogne og kølebiler. Pallekapacitet op til 8 europaller, præcis temperaturkontrol (-20°C til +25°C) og godsforsikring."
      />

      {/* Hero */}
      <section className="bg-navy-950 text-white py-16 lg:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-950 border border-sky-500/40 text-sky-300 text-xs font-bold uppercase tracking-wider">
              <i className="fa-solid fa-truck-front text-sky-400" aria-hidden="true" />
              <span>Transport4U Vognpark</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
              Vores biler og transportmuligheder
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              Vores flåde består af topmoderne køretøjer af ny klasse udstyret med avancerede klimaanlæg. Vi tilbyder høj fleksibilitet med kapacitet fra 1 kg op til 8 europaller samt specialgods efter aftale.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Comparison Highlights */}
      <section className="bg-slate-100 py-10 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            <div className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-xs">
              <span className="text-xs uppercase font-bold text-slate-500 block">Kapacitet</span>
              <span className="text-lg sm:text-xl font-black text-slate-900 mt-1 block">Op til 8 europaller</span>
            </div>
            <div className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-xs">
              <span className="text-xs uppercase font-bold text-slate-500 block">Temperaturområde</span>
              <span className="text-lg sm:text-xl font-black text-sky-700 mt-1 block">-20°C til +25°C</span>
            </div>
            <div className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-xs">
              <span className="text-xs uppercase font-bold text-slate-500 block">Godsstørrelse</span>
              <span className="text-lg sm:text-xl font-black text-slate-900 mt-1 block">Fra 1 kg</span>
            </div>
            <div className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-xs">
              <span className="text-xs uppercase font-bold text-slate-500 block">Responstid</span>
              <span className="text-lg sm:text-xl font-black text-slate-900 mt-1 block">Inden for 2 timer</span>
            </div>
          </div>
        </div>
      </section>

      {/* Fleet Listing Section (Iceotemp reference) */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header & Filter */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-sky-600">
                Køretøjsoversigt
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Vælg det rette køretøj til din transport
              </h2>
              <p className="text-slate-600 text-sm mt-1">
                Sammenlign specifikationer, kapacitet og temperaturområder.
              </p>
            </div>

            {/* Category filter pills */}
            <div className="flex flex-wrap items-center gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                    selectedCategory === cat
                      ? 'bg-sky-600 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Vehicles Grid */}
          {loading ? (
            <div className="py-16 text-center text-slate-400">
              <i className="fa-solid fa-spinner fa-spin text-2xl text-sky-500 mb-2" />
              <p className="text-sm">Henter køretøjer fra flåden...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredVehicles.map((v) => (
                <VehicleCard key={v.id} vehicle={v} />
              ))}
            </div>
          )}

          {/* Special Cargo Notice */}
          <div className="mt-14 p-6 sm:p-8 rounded-2xl bg-slate-50 border border-slate-200/90 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                <i className="fa-solid fa-circle-info text-xl" aria-hidden="true" />
              </div>
              <div>
                <h4 className="text-base font-bold text-slate-900">
                  Har du ikke-standard gods eller særlige mål?
                </h4>
                <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                  Transport4U håndterer også forsendelser med særlige dimensioner og krav. Angiv blot dine specifikationer ved bestilling, så finder vi den mest optimale køretøjsløsning.
                </p>
              </div>
            </div>

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-bold text-xs sm:text-sm transition-colors shrink-0 shadow-xs"
            >
              <span>Forespørg på specialtransport</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* CMS Gallery for Vores Biler */}
      <section className="py-20 bg-slate-50 border-t border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-sky-600">
              Vognpark Billeder
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Se vores biler fra alle vinkler
            </h2>
            <p className="text-slate-600 text-sm">
              Klik på et billede for fuld visning med zoom.
            </p>
          </div>

          <CMSGallery gallerySlug="vores-biler" />
        </div>
      </section>

      {/* Bottom Action */}
      <section className="py-16 bg-navy-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-2xl sm:text-3xl font-black text-white">
            Klar til at booke en bil?
          </h2>
          <p className="text-slate-300 text-base max-w-xl mx-auto">
            Udfyld vores bestillingsformular for hurtig ekspedition, send en e-mail til <a href="mailto:transport4u.dk@gmail.com" className="text-sky-400 hover:underline">transport4u.dk@gmail.com</a> eller ring på <a href="tel:+4550352035" className="text-sky-400 hover:underline">50 35 20 35</a>.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm transition-colors shadow-md"
            >
              <span>Udfyld bestillingsformular</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
            <a
              href="mailto:transport4u.dk@gmail.com"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-sm border border-slate-700 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-sky-400" aria-hidden="true" />
              <span>Send e-mail</span>
            </a>
            <a
              href="tel:+4550352035"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white text-xs font-semibold border border-slate-800 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              <span>Tlf: 50 35 20 35</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
